import {
  AlertCircle,
  Banknote,
  BadgeCheck,
  ChevronDown,
  ClipboardCopy,
  FilePlus2,
  MessageSquareText,
  RotateCcw,
  Undo2,
  XCircle,
  type LucideIcon,
} from "lucide-react";
import { useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAmountDisplay } from "@/shared/components/data-display/ColocaAmountDisplay";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaDropdownMenu } from "@/shared/components/overlays/ColocaDropdownMenu";
import { TwoFactorActionModals } from "@/shared/components/overlays/TwoFactorActionModals";
import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { readApiCode } from "@/shared/utils/apiError";
import { minorUnitsFor } from "@/shared/utils/currencyMinorUnits";
import { colocaToast } from "@/shared/utils/toast";

import { useIncidentReport } from "../hooks/useIncidentReport";
import { usePaymentIncident } from "../hooks/usePaymentIncident";
import { PaymentIncidentsService } from "../services/paymentIncidents.service";
import type {
  CreatePaymentIncidentEventPayload,
  PaymentIncidentReport,
  RecordableIncidentAction,
} from "../types/paymentIncidents.types";
import { defaultIncidentType, incidentProviderName, recordableActionsFor } from "../utils/paymentIncidents";
import { IncidentCaseStrip } from "./IncidentCaseStrip";
import { IncidentEventDialog } from "./IncidentEventDialog";
import { IncidentRefundDialog } from "./IncidentRefundDialog";
import { IncidentReportDialog } from "./IncidentReportDialog";
import { IncidentStatusPill } from "./IncidentStatusPill";
import { IncidentTimeline } from "./IncidentTimeline";
import { OpenIncidentDialog, type OpenIncidentValues } from "./OpenIncidentDialog";

const ACTION_ICON: Record<RecordableIncidentAction, LucideIcon> = {
  provider_responded: MessageSquareText,
  provider_refunded: Banknote,
  resolved: BadgeCheck,
  dismissed: XCircle,
  note: MessageSquareText,
  reopened: RotateCcw,
};

export interface IncidentPanelProps {
  /** The incident already open on the payout, if any. */
  incidentId: string | null;
  operation: {
    id: string;
    status: string | null;
    rail: string | null;
    providerCode: string | null;
  };
  /** The staff who may change incidents (the wallet top-ups allowlist). The rest read. */
  canManage: boolean;
  /** Something changed: the host reads its list again. */
  onChanged?: () => void;
}

/**
 * The incident of one payout, inside its detail: where the claim stands with the provider, the report to
 * paste in their channel, the follow-ups with their screenshots, and the refund to the client.
 */
export function IncidentPanel({ incidentId, operation, canManage, onChanged }: IncidentPanelProps) {
  const { t, activeLanguage } = useTranslations("paymentIncidents");
  const [currentId, setCurrentId] = useState<string | null>(incidentId);
  // The host's list may learn of an incident after this panel mounted (someone else opened one on this payout,
  // then the list refreshed): follow it. A null from the host never drops the one opened here.
  const [hostIncidentId, setHostIncidentId] = useState(incidentId);
  if (incidentId !== hostIncidentId) {
    setHostIncidentId(incidentId);
    if (incidentId) setCurrentId(incidentId);
  }
  const { incident, isLoading, error, reload, replace } = usePaymentIncident(currentId);
  const { copyReport, twoFactor: reportTwoFactor } = useIncidentReport();
  const [opening, setOpening] = useState(false);
  const [report, setReport] = useState<PaymentIncidentReport | null>(null);
  const [eventAction, setEventAction] = useState<RecordableIncidentAction | null>(null);
  const [refunding, setRefunding] = useState(false);
  const provider = incidentProviderName(operation.providerCode) ?? t("provider_fallback");

  const changed = () => onChanged?.();

  const runReport = async (id: string) => {
    try {
      const result = await copyReport(id);
      if (!result) return;
      replace(result.incident);
      setReport(result);
      changed();
    } catch (failure: unknown) {
      colocaToast.error(HttpApiService.getErrorMessage(failure));
    }
  };

  const open = async (values: OpenIncidentValues, thenReport: boolean) => {
    let id: string;
    try {
      const created = await PaymentIncidentsService.create({ operation_id: operation.id, ...values });
      replace(created);
      id = created.id;
    } catch (failure: unknown) {
      // Someone opened one on this payout first (or this list was read before they did): follow theirs.
      // Read whole, so the panel shows it (and its 2FA challenge) at once instead of a skeleton.
      const existing =
        readApiCode(failure) === "incident_already_exists"
          ? await PaymentIncidentsService.findByOperation(operation.id)
              .then((found) => (found ? PaymentIncidentsService.get(found.id) : null))
              .catch(() => null)
          : null;
      if (!existing) throw new Error(HttpApiService.getErrorMessage(failure));
      replace(existing);
      id = existing.id;
      colocaToast.info(t("open_already_exists"));
    }
    setCurrentId(id);
    setOpening(false);
    changed();
    if (thenReport) await runReport(id);
  };

  const record = async (payload: CreatePaymentIncidentEventPayload) => {
    if (!incident) return;
    try {
      const next = await PaymentIncidentsService.addEvent(incident.id, payload);
      replace(next);
      setEventAction(null);
      colocaToast.success(t(`event_saved_${payload.action}`, { provider }));
      changed();
    } catch (failure: unknown) {
      throw new Error(HttpApiService.getErrorMessage(failure));
    }
  };

  if (!currentId) {
    return (
      <section
        aria-labelledby="incident-panel-title"
        className="space-y-3 rounded-xl border border-dashed border-border p-4"
      >
        <h3 id="incident-panel-title" className="text-sm font-semibold text-foreground">
          {t("panel_title")}
        </h3>
        <p className="text-sm text-muted-foreground">{t("panel_empty", { provider })}</p>
        {canManage ? (
          <ColocaButton variant="secondary" size="sm" icon={FilePlus2} onClick={() => setOpening(true)}>
            {t("panel_open")}
          </ColocaButton>
        ) : (
          <p className="text-xs text-muted-foreground">{t("panel_read_only")}</p>
        )}
        <OpenIncidentDialog
          open={opening}
          onOpenChange={setOpening}
          provider={provider}
          defaultType={defaultIncidentType({
            operation_status: operation.status,
            operation_rail: operation.rail,
          })}
          onSubmit={open}
        />
      </section>
    );
  }

  if (!incident) {
    return (
      <section className="space-y-3 rounded-xl border border-border p-4" aria-busy={isLoading}>
        {error ? (
          <ColocaAlert
            variant="destructive"
            icon={AlertCircle}
            text={error}
            actions={
              <ColocaButton variant="secondary" size="sm" onClick={reload}>
                {t("action_retry")}
              </ColocaButton>
            }
          />
        ) : (
          <>
            <ColocaSkeleton className="h-5 w-40" />
            <ColocaSkeleton className="h-10 w-full" />
            <ColocaSkeleton className="h-20 w-full" />
          </>
        )}
      </section>
    );
  }

  const refund = incident.refund;
  const reporting = reportTwoFactor.isRunning;
  const actions = recordableActionsFor(incident.status);

  return (
    <section aria-labelledby="incident-panel-title" className="space-y-5 rounded-xl border border-border p-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 id="incident-panel-title" className="text-sm font-semibold text-foreground">
            {t("panel_title_with_reference", { reference: incident.reference })}
          </h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {t(`type_${incident.type}`)} · {t("panel_opened_by", { email: incident.opened_by_email })}
          </p>
        </div>
        <IncidentStatusPill status={incident.status} provider={provider} />
      </header>

      <IncidentCaseStrip incident={incident} provider={provider} />

      <dl className="grid gap-3 text-sm sm:grid-cols-3">
        <div className="min-w-0">
          <dt className="text-xs text-muted-foreground">{t("panel_at_stake")}</dt>
          <dd className="mt-0.5 font-medium text-foreground">
            <ColocaAmountDisplay
              value={incident.amount_at_stake}
              currency={incident.currency}
              fractionDigits={minorUnitsFor(incident.currency)}
              locale={activeLanguage}
              size="sm"
            />
          </dd>
        </div>
        <div className="min-w-0">
          <dt className="text-xs text-muted-foreground">{t("panel_case_reference", { provider })}</dt>
          <dd className="mt-0.5 truncate font-mono text-sm text-foreground">
            {incident.provider_case_reference ?? "—"}
          </dd>
        </div>
        <div className="min-w-0">
          <dt className="text-xs text-muted-foreground">{t("panel_client_refund")}</dt>
          <dd className="mt-0.5 text-foreground">
            {refund.top_up ? (
              <span className="flex flex-wrap items-center gap-1.5">
                <ColocaAmountDisplay
                  value={refund.top_up.amount}
                  currency={refund.top_up.currency}
                  fractionDigits={minorUnitsFor(refund.top_up.currency)}
                  locale={activeLanguage}
                  size="sm"
                />
                <span className="font-mono text-xs text-muted-foreground">{refund.top_up.reference}</span>
              </span>
            ) : (
              "—"
            )}
          </dd>
        </div>
      </dl>

      {canManage ? (
        <div className="flex flex-wrap gap-2">
          <ColocaButton
            variant="primary"
            size="sm"
            icon={ClipboardCopy}
            loading={reporting}
            disabled={reporting}
            onClick={() => void runReport(incident.id)}
          >
            {t(incident.reported_at ? "panel_copy_follow_up" : "panel_copy_report", { provider })}
          </ColocaButton>
          <ColocaDropdownMenu
            trigger={
              <ColocaButton variant="default" size="sm" iconTrailing={ChevronDown}>
                {t("panel_record")}
              </ColocaButton>
            }
            align="start"
            items={actions.map((action) => ({
              label: t(`record_${action}`, { provider }),
              icon: ACTION_ICON[action],
              variant: action === "dismissed" ? "destructive" : "default",
              onClick: () => setEventAction(action),
            }))}
          />
          {refund.allowed && (
            <ColocaButton variant="default" size="sm" icon={Undo2} onClick={() => setRefunding(true)}>
              {t("panel_refund")}
            </ColocaButton>
          )}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">{t("panel_read_only")}</p>
      )}
      {reporting && (
        <p role="status" className="text-xs text-muted-foreground">
          {t("panel_reading_provider", { provider })}
        </p>
      )}
      {canManage && !refund.allowed && refund.blocked_by && refund.blocked_by !== "already_refunded" && (
        <p className="text-xs text-muted-foreground">{t(`refund_blocked_${refund.blocked_by}`)}</p>
      )}

      <div className="space-y-2">
        <h4 className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
          {t("timeline_title")}
        </h4>
        <IncidentTimeline events={incident.events} provider={provider} />
      </div>

      <IncidentReportDialog report={report} provider={provider} onClose={() => setReport(null)} />
      <IncidentEventDialog
        action={eventAction}
        incident={incident}
        provider={provider}
        onClose={() => setEventAction(null)}
        onSubmit={record}
      />
      <IncidentRefundDialog
        open={refunding}
        incident={incident}
        provider={provider}
        onClose={() => setRefunding(false)}
        onRefunded={(next) => {
          replace(next);
          changed();
        }}
        onStale={reload}
      />
      <TwoFactorActionModals
        controller={reportTwoFactor}
        requiredDescription={t("report_two_factor_required")}
        challengeDescription={t("report_two_factor_description", { provider })}
      />
    </section>
  );
}
